import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import useAtsInterviewsApi from './config';
import ScopeBanner from '../ats-applications/ScopeBanner';
import { parseTableParams, toUtcIso } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import A11yDialog from '../../components/ui/A11yDialog';
import { useAuth } from '../../context/AuthContext';
import ScorecardFillDialog from './ScorecardFillDialog';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput.jsx';
import GenericSelect from '../../components/ui/GenericSelect';
import DateTimePicker from '../../components/ui/DateTimePicker';

const Container = styled.div``;

const Modal = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(p) => (p.$open ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1300;
  padding: 24px;

  /* FE-08 — telefonda 24px x 2 = 48px, 390px ekranin %12'si. */
  @media (max-width: 600px) {
    padding: 12px;
  }
`;
const ModalCard = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 480px;
  width: 100%;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  /* FE-08 — max-height YOKTU: perde "position: fixed; inset: 0" +
     "align-items: center" olduğundan, karttan uzun bir içerik ekranın ALTINDAN
     VE ÜSTÜNDEN taşıyordu ve hiçbir yolla kaydırılamıyordu — "Onayla"
     düğmesine telefonda hiç ulaşılamıyordu. "100%" perdenin iç kutusuna göre
     çözülür, yani perdenin iç boşluğu 24px'ten 12px'e inince kart otomatik
     olarak büyür (sabit 90vh bunu yapamaz).
     Bu, kabuğun "tek kaydırma" kuralını ÇİĞNEMEZ: diyalog ayrı bir katmandır,
     altındaki Main bu sırada zaten kilitlidir. */
  max-height: 100%;
  overflow-y: auto;
`;
// FE-24 — ham <input type="datetime-local"> / <select> / <textarea> ve
// hardcoded renkler kaldırıldı: ev tasarım dili DateTimePicker (klavyeyle
// yazılabilir) + GenericSelect + TextInput. Field yalnız dikey boşluk kabıdır.
const Field = styled.div`
  margin-bottom: 14px;
`;
const Actions = styled.div` display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; `;
// #72 — yerel Primary/Secondary (kopyalanmis blok — onboarding-*/ats-*
// sayfalarinda ayni tarif) kaldirildi, paylasilan Button'a baglandi
// (bkz. t6-report.md).

// ISO (UTC) → tarayıcı-yerel "yyyy-MM-ddTHH:mm". Yeniden planlama diyaloğu
// mevcut randevuyu ÖN DOLDURUR: kullanıcı bir turu 1 saat ötelemek için iki tam
// tarih-saati sıfırdan yazmak zorunda kalmamalı (FE-24). InterviewScheduleDialog
// ile aynı biçim.
const toLocalDtString = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export default function AtsInterviewsPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { canWrite } = useAuth();
  const writable = canWrite ? canWrite('AT') : false;

  // Derin bağlantı kapsamı: başvuru detayının "Planlanmış Mülakatlar" bölümü
  // (?applicationId=…&candidateName=…) listeyi TEK başvuruya daraltır. Kapsam
  // tablo süzgecinin ÜSTÜNE eklenir ve şeritte GÖRÜNÜR durur — görünmez bir
  // süzgeç, "mülakatlarım nerede?" sorusunu doğurur.
  const scope = useMemo(() => {
    const q = new URLSearchParams(location.search || '');
    return {
      applicationId: q.get('applicationId') || null,
      candidateName: q.get('candidateName') || null,
    };
  }, [location.search]);

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const [completeDialog, setCompleteDialog] = useState({ isOpen: false, id: null, candidate: '' });
  const [cancelDialog, setCancelDialog] = useState({ isOpen: false, id: null, candidate: '', reason: '' });
  const [rescheduleDialog, setRescheduleDialog] = useState({ isOpen: false, row: null, start: '', end: '', reason: '' });
  const [noShowDialog, setNoShowDialog] = useState({ isOpen: false, id: null, candidate: '', side: 'candidate' });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, id: null, candidate: '' });
  const [scorecardDialog, setScorecardDialog] = useState({ isOpen: false, interview: null });
  const [busy, setBusy] = useState(false);

  const effectiveParams = useMemo(() => (
    scope.applicationId
      ? { ...queryParams, 'applicationId[eq]': scope.applicationId }
      : queryParams
  ), [queryParams, scope.applicationId]);

  const {
    fetching, data, pagination, columns: baseColumns,
    complete, cancel, reschedule, markNoShow, remove, refresh,
  } = useAtsInterviewsApi(effectiveParams);

  const clearScope = useCallback(() => history.push('/ats/interviews'), [history]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => setFilterList(newFilterList), []);

  // FE-28 — mülakat satırından adaya/başvuruya gitmenin yolu YOKTU. Ayrı bir
  // mülakat detay sayfası olmadığından satır başvuru detayına götürür
  // (skorkart listesindeki tıklama hedefiyle AYNI).
  const handleRowClick = useCallback((row) => {
    if (row?.applicationId) history.push(`/ats/applications/${row.applicationId}`);
  }, [history]);

  const buildActions = useCallback((row) => {
    if (!row) return [];
    const actions = [];
    // Skorkart yalnız YAPILMIŞ (completed) mülakatta doldurulur — no_show dahil
    // diğer tüm durumlarda mülakat gerçekleşmediği için değerlendirme girilemez;
    // backend aynı kuralı uygular (ats.scorecard.interviewNotCompleted).
    // Kim doldurabilir: AT.Write/admin ya da aktif katılımcı — sunucu `canScore`
    // ile söyler (SubmitResponse kapısının aynası). Doldur/Düzenle ayrımı "BEN
    // doldurdum mu"dur (`hasMyResponse`); `hasScorecardResponse` herhangi bir
    // değerlendiriciyi sayar ve ikinci panelisti "Düzenle"ye yanıltıyordu.
    if (row.status === 'completed' && row.canScore) {
      actions.push({
        type: 'scorecard',
        label: row.hasMyResponse
          ? t('ats.interview.editScorecard')
          : t('ats.interview.fillScorecard'),
        onClick: () => setScorecardDialog({ isOpen: true, interview: row }),
      });
    }
    // Panel debrief (SPEC F4): tamamlanmış mülakatta HERKESE (AT.Read) açılır —
    // görünürlük kuralı sayfanın ucundadır (anchoring: kartını göndermeyen
    // katılımcı açıklayıcı boş durum görür; katılımcı olmayan HM muaftır).
    // Aksiyonu burada daraltmak HM'yi (canScore=false, writable=false) dışarıda bırakırdı.
    if (row.status === 'completed') {
      actions.push({
        type: 'debrief',
        label: t('ats.debrief.action'),
        onClick: () => history.push(`/ats/interviews/${row.id}/debrief`),
      });
    }
    if (row.status === 'scheduled') {
      // Tamamlama katılımcı/koordinatöre de açık (AT.Read) — sunucu kapısıyla aynı.
      if (writable || row.canScore) {
        actions.push({ type: 'complete', label: t('ats.interview.action.complete'),
          onClick: () => setCompleteDialog({ isOpen: true, id: row.id, candidate: row.candidateFullName }) });
      }
      if (writable) {
        actions.push({ type: 'reschedule', label: t('ats.interview.action.reschedule'),
          onClick: () => setRescheduleDialog({
            isOpen: true,
            row,
            // Mevcut randevu ön dolar — boş diyalog kullanıcıyı iki tam
            // tarih-saati yeniden yazmaya zorluyordu (FE-24).
            start: toLocalDtString(row.scheduledStart),
            end: toLocalDtString(row.scheduledEnd),
            reason: '',
          }) });
        actions.push({ type: 'no_show', label: t('ats.interview.action.noShow'),
          onClick: () => setNoShowDialog({ isOpen: true, id: row.id, candidate: row.candidateFullName, side: 'candidate' }) });
        actions.push({ type: 'cancel', label: t('ats.interview.action.cancel'),
          onClick: () => setCancelDialog({ isOpen: true, id: row.id, candidate: row.candidateFullName, reason: '' }) });
        actions.push({ type: 'divider' });
      }
    }
    if (writable) {
      actions.push({ type: 'delete', label: t('common.delete'),
        onClick: () => setDeleteDialog({ isOpen: true, id: row.id, candidate: row.candidateFullName }) });
    }
    return actions;
  }, [t, writable, history]);

  // ESC, İptal düğmesi ve atma onayı AYNI kapatma yolunu kullanır (FE-25).
  const closeCancelDialog = useCallback(() => setCancelDialog({ isOpen: false, id: null, candidate: '', reason: '' }), []);
  const closeRescheduleDialog = useCallback(() => setRescheduleDialog({ isOpen: false, row: null, start: '', end: '', reason: '' }), []);
  const closeNoShowDialog = useCallback(() => setNoShowDialog({ isOpen: false, id: null, candidate: '', side: 'candidate' }), []);

  const noShowSideOptions = useMemo(() => ['candidate', 'interviewer', 'both'].map((k) => ({
    id: k, name: t(`ats.interview.noShow.${k}`),
  })), [t]);

  // Aksiyon kolonu HER ZAMAN çizilir — yazma yetkisi olmayan katılımcının da
  // skorkart / tamamla aksiyonu var. Aksiyonu olmayan satırda hücre boş kalır.
  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions', label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          const actions = buildActions(row);
          return actions.length > 0 ? <OptionsMenu actions={actions} row={row} /> : null;
        },
      },
    },
  ], [baseColumns, data, buildActions]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={completeDialog.isOpen}
        title={t('ats.interview.action.complete')}
        message={t('ats.interview.completeConfirm', { candidate: completeDialog.candidate })}
        onConfirm={async () => { setBusy(true); await complete(completeDialog.id); setCompleteDialog({ isOpen: false, id: null, candidate: '' }); setBusy(false); }}
        onCancel={() => setCompleteDialog({ isOpen: false, id: null, candidate: '' })}
        loading={busy}
      />

      <A11yDialog
        open={cancelDialog.isOpen}
        onClose={closeCancelDialog}
        dirty={!!(cancelDialog.reason || '').trim()}
        overlay={Modal}
        card={ModalCard}
        overlayProps={{ $open: true }}
      >
        {({ titleId }) => (
          <>
            <h3 id={titleId} style={{ margin: '0 0 16px' }}>{t('ats.interview.action.cancel')} — {cancelDialog.candidate}</h3>
            <Field>
              <TextInput
                label={t('ats.interview.cancelReason')}
                type="textarea"
                rows={3}
                value={cancelDialog.reason}
                onChange={(e) => setCancelDialog({ ...cancelDialog, reason: e.target.value })}
              />
            </Field>
            <Actions>
              <Button variant="outline" onClick={closeCancelDialog}>{t('common.cancel')}</Button>
              <Button disabled={busy} loading={busy} onClick={async () => {
                setBusy(true);
                const ok = await cancel(cancelDialog.id, cancelDialog.reason || undefined);
                // FE-31 — yalnız BAŞARIDA kapan; sunucu reddederse yazılan iptal
                // gerekçesi kaybolmasın.
                if (ok) closeCancelDialog();
                setBusy(false);
              }}>{t('common.confirm')}</Button>
            </Actions>
          </>
        )}
      </A11yDialog>

      <A11yDialog
        open={rescheduleDialog.isOpen}
        onClose={closeRescheduleDialog}
        dirty={!!(rescheduleDialog.reason || '').trim()}
        overlay={Modal}
        card={ModalCard}
        overlayProps={{ $open: true }}
      >
        {({ titleId }) => (
          <>
            <h3 id={titleId} style={{ margin: '0 0 16px' }}>{t('ats.interview.action.reschedule')} — {rescheduleDialog.row?.candidateFullName}</h3>
            <Field>
              <DateTimePicker
                label={t('ats.interview.scheduledStart')}
                mode="datetime"
                value={rescheduleDialog.start}
                onChange={(v) => setRescheduleDialog((prev) => ({ ...prev, start: v || '' }))}
              />
            </Field>
            <Field>
              <DateTimePicker
                label={t('ats.interview.scheduledEnd')}
                mode="datetime"
                value={rescheduleDialog.end}
                onChange={(v) => setRescheduleDialog((prev) => ({ ...prev, end: v || '' }))}
              />
            </Field>
            <Field>
              <TextInput
                label={t('ats.interview.cancelReason')}
                type="textarea"
                rows={3}
                value={rescheduleDialog.reason}
                onChange={(e) => setRescheduleDialog({ ...rescheduleDialog, reason: e.target.value })}
              />
            </Field>
            <Actions>
              <Button variant="outline" onClick={closeRescheduleDialog}>{t('common.cancel')}</Button>
              <Button
                disabled={busy || !rescheduleDialog.start || !rescheduleDialog.end}
                loading={busy}
                onClick={async () => {
                  setBusy(true);
                  const ok = await reschedule(rescheduleDialog.row.id, {
                    // Absolute UTC — datetime-local values are browser-local (see toUtcIso).
                    scheduledStart: toUtcIso(rescheduleDialog.start),
                    scheduledEnd: toUtcIso(rescheduleDialog.end),
                    reason: rescheduleDialog.reason || undefined,
                  });
                  // FE-31 — sunucu çakışma/izin hatası döndürdüğünde yeniden
                  // yazılan tarih-saat çifti KORUNUR.
                  if (ok) closeRescheduleDialog();
                  setBusy(false);
                }}
              >{t('common.confirm')}</Button>
            </Actions>
          </>
        )}
      </A11yDialog>

      <A11yDialog
        open={noShowDialog.isOpen}
        onClose={closeNoShowDialog}
        overlay={Modal}
        card={ModalCard}
        overlayProps={{ $open: true }}
      >
        {({ titleId }) => (
          <>
            <h3 id={titleId} style={{ margin: '0 0 16px' }}>{t('ats.interview.action.noShow')} — {noShowDialog.candidate}</h3>
            <Field>
              <GenericSelect
                label={t('ats.interview.noShowSide')}
                options={noShowSideOptions}
                value={noShowDialog.side}
                onChange={(id) => setNoShowDialog({ ...noShowDialog, side: id || 'candidate' })}
                required
              />
            </Field>
            <Actions>
              <Button variant="outline" onClick={closeNoShowDialog}>{t('common.cancel')}</Button>
              <Button disabled={busy} loading={busy} onClick={async () => {
                setBusy(true);
                const ok = await markNoShow(noShowDialog.id, noShowDialog.side);
                if (ok) closeNoShowDialog();
                setBusy(false);
              }}>{t('common.confirm')}</Button>
            </Actions>
          </>
        )}
      </A11yDialog>

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        title={t('common.delete')}
        message={t('ats.interview.deleteConfirm', { candidate: deleteDialog.candidate })}
        onConfirm={async () => { setBusy(true); await remove(deleteDialog.id); setDeleteDialog({ isOpen: false, id: null, candidate: '' }); setBusy(false); }}
        onCancel={() => setDeleteDialog({ isOpen: false, id: null, candidate: '' })}
        loading={busy}
      />

      <ScorecardFillDialog
        open={scorecardDialog.isOpen}
        interview={scorecardDialog.interview}
        onClose={() => setScorecardDialog({ isOpen: false, interview: null })}
        onSaved={() => refresh()}
      />

      {scope.applicationId && (
        <ScopeBanner
          candidateName={scope.candidateName || t('ats.interview.scope.singleApplication')}
          onClear={clearScope}
        />
      )}

      {useMemo(() => (
        <DataTable
          title={t('ats.interview.pageTitle')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange, handleRowClick,
      ])}
    </Container>
  );
}
